/**
 * Bond Mentor — Sound Effects
 *
 * Small, dependency-free alert tones generated with the Web Audio API.
 * No audio files are needed, so this works offline and adds nothing to the
 * bundle size.
 *
 * Browsers block audio until the user has interacted with the page (the
 * "autoplay policy"). To respect that without spamming console warnings:
 *   - The AudioContext is created/resumed ONLY inside a real user gesture
 *     (a one-time pointer/key/touch listener unlocks it).
 *   - playAlertSound() plays only when the context is already running;
 *     otherwise it returns quietly. So a background "new notification" chime
 *     that arrives before any click is silently skipped instead of warning.
 */

let audioCtx = null;
let unlockInstalled = false;

// True only once the user has genuinely interacted with the page. We never
// create or resume audio before this, which is what avoids Chrome's
// "AudioContext was not allowed to start" console warning.
function pageHasBeenActivated() {
  if (typeof navigator !== 'undefined' && navigator.userActivation) {
    return navigator.userActivation.hasBeenActive === true;
  }
  return null; // API unavailable — fall back to gesture listeners only
}

function createContext() {
  if (typeof window === 'undefined') return null;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!audioCtx) {
    // Do not construct until the page has been activated; constructing earlier
    // is exactly what triggers the browser warning.
    if (pageHasBeenActivated() === false) return null;
    try { audioCtx = new Ctx(); } catch { return null; }
  }
  return audioCtx;
}

/**
 * Install a one-time listener that creates and resumes the AudioContext on the
 * first genuine user interaction. `click`/`keydown`/`touchend` are reliable
 * activation events (unlike `pointerdown`, which can fire before activation is
 * registered and still warn).
 */
export function installAudioUnlock() {
  if (unlockInstalled || typeof window === 'undefined') return;
  unlockInstalled = true;
  const unlock = () => {
    const ctx = createContext();
    if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
    if (ctx && ctx.state === 'running') {
      window.removeEventListener('click', unlock);
      window.removeEventListener('keydown', unlock);
      window.removeEventListener('touchend', unlock);
    }
  };
  window.addEventListener('click', unlock, { passive: true });
  window.addEventListener('keydown', unlock, { passive: true });
  window.addEventListener('touchend', unlock, { passive: true });
}

/**
 * Play a short, soft chime. `variant`:
 *   'preview' — single confirming note when the user turns sounds on
 *   'alert'   — gentle two-note rising chime for a new notification
 *   'warning' — slightly lower, more urgent two-note chime
 *
 * `force` is used by the preview (called directly from a click), which is a
 * user gesture, so it may create/resume the context right then.
 */
export function playAlertSound(variant = 'alert', { force = false } = {}) {
  const ctx = force ? createContext() : audioCtx;
  if (!ctx) return false;

  // Outside a user gesture we never resume; if the context isn't running yet,
  // skip silently rather than triggering a browser warning.
  if (ctx.state === 'suspended') {
    if (force) {
      ctx.resume().catch(() => {});
    } else {
      return false;
    }
  }
  if (ctx.state !== 'running' && !force) return false;

  const now = ctx.currentTime;
  const master = ctx.createGain();
  master.gain.value = 0.0001;
  master.connect(ctx.destination);

  const peak = 0.16;
  const tones =
    variant === 'preview'
      ? [{ f: 880, t: 0, d: 0.18 }]
      : variant === 'warning'
        ? [{ f: 392, t: 0, d: 0.16 }, { f: 330, t: 0.14, d: 0.22 }]
        : [{ f: 587, t: 0, d: 0.15 }, { f: 784, t: 0.13, d: 0.22 }];

  tones.forEach(({ f, t, d }) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = f;
    gain.gain.setValueAtTime(0.0001, now + t);
    gain.gain.exponentialRampToValueAtTime(peak, now + t + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + t + d);
    osc.connect(gain);
    gain.connect(master);
    osc.start(now + t);
    osc.stop(now + t + d + 0.02);
  });

  master.gain.setValueAtTime(1, now);
  return true;
}

export default playAlertSound;
